import { useCallback, useState } from 'react'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useApolloClient, useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import { canEditNoteAnnotations } from './noteContent'
import type { NoteContent } from './noteContent'

import { CollaborationNoteWithActionsFields } from './notesQueries'
import { replaceUpdatedNote } from './notesCache'
import type { CollaborationNote } from './notesQueries'
import type { NoteSubject } from './noteSubject'
import { useScopedNoteWrite } from './useScopedNoteWrite'

export const UpdateCollaborationNoteMutation = graphql(
  `
    mutation UpdateCollaborationNote($input: UpdateCollaborationNoteInput!) {
      updateCollaborationNote(input: $input) {
        __typename
        ... on UpdateCollaborationNoteSuccess {
          note {
            ...CollaborationNoteWithActionsFields
          }
        }
        ... on InvalidInputError {
          message
        }
      }
    }
  `,
  [CollaborationNoteWithActionsFields],
)

export type UpdateNoteFailure = 'validation' | 'permission' | 'request'
export type UpdateNoteResult =
  | { ok: true }
  | { ok: false; reason: UpdateNoteFailure }

type ScopedError = {
  selectionKey: string
  noteId: string
  reason: UpdateNoteFailure
}

type ConfirmedNote = { subject: NoteSubject; note: CollaborationNote }

export function useUpdateNote(subject: NoteSubject): {
  submit: (
    note: CollaborationNote,
    content: NoteContent,
  ) => Promise<UpdateNoteResult>
  pending: boolean
  errorFor: (noteId: string) => UpdateNoteFailure | null
  clearError: (noteId: string) => void
  failRequest: (noteId: string) => void
} {
  const client = useApolloClient()
  const applyConfirmed = useCallback(
    ({ subject: confirmedSubject, note }: ConfirmedNote): void =>
      replaceUpdatedNote(client.cache, confirmedSubject, note),
    [client.cache],
  )
  const { scope, scopeKey, isCurrent, pending, run, keepConfirmed } =
    useScopedNoteWrite(subject, applyConfirmed)
  const [scopedError, setScopedError] = useState<ScopedError | null>(null)
  const [mutate] = useMutation(UpdateCollaborationNoteMutation, {
    errorPolicy: 'all',
  })

  const submit = useCallback(
    async (
      note: CollaborationNote,
      { text, annotations }: NoteContent,
    ): Promise<UpdateNoteResult> => {
      if (!scope || !scopeKey) {
        return { ok: false, reason: 'permission' }
      }
      if (
        !note.allowedActions.includes('updateNote') ||
        !canEditNoteAnnotations(note.text, note.annotations)
      ) {
        setScopedError({
          selectionKey: scopeKey,
          noteId: note.id,
          reason: 'permission',
        })
        return { ok: false, reason: 'permission' }
      }
      if (!text.trim() || text.length > 500) {
        setScopedError({
          selectionKey: scopeKey,
          noteId: note.id,
          reason: 'validation',
        })
        return { ok: false, reason: 'validation' }
      }

      const outcome = await run(
        scopeKey,
        async (): Promise<UpdateNoteFailure | null> => {
          setScopedError(null)
          try {
            const response = await mutate({
              variables: { input: { noteId: note.id, text, annotations } },
              fetchPolicy: 'no-cache',
            })
            const payload = response.data?.updateCollaborationNote
            if (payload?.__typename === 'UpdateCollaborationNoteSuccess') {
              if (isCurrent(scopeKey)) {
                replaceUpdatedNote(client.cache, scope.subject, payload.note)
              } else {
                keepConfirmed(scopeKey, {
                  subject: scope.subject,
                  note: payload.note,
                })
              }
              return null
            }
            if (response.error) {
              return CombinedGraphQLErrors.is(response.error) &&
                response.error.errors.some(
                  (error) => error.extensions?.code === 'FORBIDDEN',
                )
                ? 'permission'
                : 'request'
            }
            return payload?.__typename === 'InvalidInputError'
              ? 'validation'
              : 'request'
          } catch {
            return 'request'
          }
        },
      )
      if (!outcome.ran) return { ok: false, reason: 'request' }
      const reason = outcome.result
      if (reason === null) return { ok: true }

      if (isCurrent(scopeKey)) {
        setScopedError({ selectionKey: scopeKey, noteId: note.id, reason })
      }
      return { ok: false, reason }
    },
    [client.cache, isCurrent, keepConfirmed, mutate, run, scope, scopeKey],
  )

  const failRequest = useCallback(
    (noteId: string): void => {
      if (!scopeKey) return
      setScopedError({ selectionKey: scopeKey, noteId, reason: 'request' })
    },
    [scopeKey],
  )

  return {
    submit,
    pending,
    clearError: (noteId) =>
      setScopedError((current) =>
        current?.selectionKey === scopeKey && current.noteId === noteId
          ? null
          : current,
      ),
    failRequest,
    errorFor: (noteId) =>
      scopedError?.selectionKey === scopeKey && scopedError.noteId === noteId
        ? scopedError.reason
        : null,
  }
}
